import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { PushToggle } from "@/components/PushToggle";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  BookOpen, HandCoins, CreditCard, ArrowLeftRight, Eye, EyeOff, QrCode, Users, FileText, GraduationCap,
  Wallet, Coins, Layers, Sprout, Lock, LifeBuoy, ChevronRight, Download, Loader2, User, IdCard, CalendarDays, CircleCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PhotoUploader } from "@/components/PhotoUploader";
import { InstallBanner } from "@/components/InstallApp";
import { AccountNo } from "@/components/AccountNo";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/api";
import { fmtDate, rp, fmtAcc } from "@/lib/format";

const MENU = [
  { label: "Buku Tabungan", icon: BookOpen, to: "/app/tabungan", id: "savings", tone: "from-blue-500 to-blue-700 shadow-blue-500/40" },
  { label: "Pinjaman", icon: HandCoins, to: "/app/pinjaman", id: "loans", tone: "from-orange-400 to-orange-600 shadow-orange-500/40" },
  { label: "Pembayaran", icon: CreditCard, to: "/app/transaksi/bayar", id: "payments", tone: "from-red-500 to-red-700 shadow-red-500/40" },
  { label: "Transfer Online", icon: ArrowLeftRight, to: "/app/transaksi/transfer?ke=anggota", id: "transfer", tone: "from-violet-500 to-violet-700 shadow-violet-500/40" },
  { label: "QR Saya", sub: "(Terima Transfer)", icon: QrCode, action: "qr", id: "my-qr", tone: "from-green-500 to-green-700 shadow-green-500/40" },
  { label: "Profil Anggota", icon: Users, to: "/app/profil", id: "profile", tone: "from-indigo-500 to-violet-700 shadow-indigo-500/40" },
  { label: "Riwayat Transaksi", icon: FileText, to: "/app/transaksi", id: "history", tone: "from-sky-400 to-blue-600 shadow-sky-500/40" },
  { label: "Pendidikan Anggota", icon: GraduationCap, to: "/app/edukasi", id: "edukasi", tone: "from-pink-500 to-rose-600 shadow-pink-500/40" },
];

const ROWS_Y = ["52.6%", "60.1%", "67.7%"];
const CardRow = ({ icon: Icon, label, children, testid, i }) => (
  <div className="absolute left-[26.9%] right-[21%] flex items-center -translate-y-1/2 whitespace-nowrap" style={{ top: ROWS_Y[i] }}>
    <Icon className="h-[3.4cqw] w-[3.4cqw] text-[#1e293b] shrink-0" strokeWidth={1.6} />
    <span className="absolute left-[11.5%] text-[2.45cqw] text-[#1e293b]">{label}</span>
    <span className="absolute left-[43.6%] text-[2.7cqw] font-extrabold text-[#0b1437] flex items-center" data-testid={testid}>:&nbsp;{children}</span>
  </div>
);

export const MemberCard = ({ user, cardRef, demo }) => (
  <div ref={cardRef} className="relative overflow-hidden rounded-2xl aspect-[1672/941] shadow-xl shadow-blue-900/25 fade-up bg-white keep-white [container-type:inline-size] font-display" data-testid="member-card">
    <img src="/member-card-bg.webp" alt="" className="absolute inset-0 h-full w-full object-cover scale-[1.02] pointer-events-none select-none" draggable="false" />
    <div className="absolute left-[4.9%] top-[38.8%] w-[15.43%] h-[30.1%]">
      {!demo && <PhotoUploader bare className="h-full w-full rounded-[2.1cqw]" testid="member-card-photo" />}
    </div>
    <div className={`absolute left-[26.2%] top-[34.6%] max-w-[52%] leading-[1.15] font-extrabold text-[#0f1b4d] truncate ${(user.name || "").length > 26 ? "text-[2.9cqw] top-[36.2%]" : (user.name || "").length > 18 ? "text-[3.5cqw] top-[35.4%]" : "text-[4.4cqw]"}`} data-testid="member-card-name" title={user.name}>{user.name}</div>
    <div className="absolute left-[26.2%] top-[45.5%] w-[9.1%] h-[0.95%] rounded-full bg-gradient-to-r from-[#dc2626] via-[#dc2626] to-[#fca5a5]" />
    <CardRow i={0} icon={User} label="No. Anggota" testid="member-card-number">{user.member_no}</CardRow>
    {user.account_no && (
      <CardRow i={1} icon={IdCard} label="No. Rekening">
        <AccountNo value={user.account_no} className="text-[#0b1437] text-[2.7cqw] tracking-normal font-display font-extrabold" testid="member-card-account" />
      </CardRow>
    )}
    <CardRow i={user.account_no ? 2 : 1} icon={CalendarDays} label="Bergabung">{fmtDate(user.joined_at)}</CardRow>
    <span data-testid="member-card-status"
      className={`absolute left-[26.7%] top-[74.1%] w-[16%] h-[8.3%] rounded-full flex items-center justify-center gap-[0.9cqw] text-white text-[2.6cqw] font-extrabold shadow-sm ${user.status === "aktif" ? "bg-[#16a34a]" : "bg-[#64748b]"}`}>
      <CircleCheck className="h-[2.6cqw] w-[2.6cqw]" strokeWidth={2.4} />{user.status === "aktif" ? "Aktif" : "Nonaktif"}
    </span>
    <div className="absolute left-[79.4%] top-[60%] w-[16.1%] h-[28.5%] bg-white keep-white rounded-[2cqw] p-[1.3cqw] flex items-center justify-center" data-testid="member-card-qr">
      <QRCodeSVG value={`CUTUAHTALINO:${user.member_no}`} size={256} style={{ width: "100%", height: "100%" }} />
    </div>
  </div>
);

const BalanceCard = ({ b }) => {
  const [show, setShow] = useState(false);
  const locked = [["pokok", "Pokok", Coins], ["wajib", "Wajib", Layers]];
  const hide = (v, mask = "•••••") => (show ? rp(v) : mask);
  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white p-4 shadow-xl shadow-blue-900/30 fade-up" style={{ animationDelay: "80ms" }} data-testid="home-balance-card">
      <svg className="absolute right-4 top-3 h-20 w-32 opacity-30" viewBox="0 0 120 80" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <rect key={i} x={10 + i * 26} y={60 - i * 14} width="16" height={20 + i * 14} rx="3" fill="#93c5fd" />)}
        <path d="M8 50 L40 36 L66 40 L104 10" stroke="#e0f2fe" strokeWidth="4" fill="none" strokeLinecap="round" />
      </svg>
      <div className="relative flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg shrink-0"><Wallet className="h-7 w-7" /></div>
        <div className="border-l border-white/40 pl-4 min-w-0">
          <div className="flex items-center gap-2 text-sm text-blue-50">
            Saldo Tersedia
            <button onClick={() => setShow(!show)} data-testid="home-toggle-balance" className="p-1 rounded-full hover:bg-white/15" aria-label="Tampilkan saldo">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="text-[26px] font-extrabold font-display leading-tight" data-testid="home-available-balance">{hide(b?.sukarela, "Rp •••••••")}</div>
          <div className="text-[10.5px] text-emerald-200 font-semibold flex items-center gap-1"><Sprout className="h-3 w-3" /> Simpanan sukarela • bisa ditransfer & ditarik</div>
        </div>
      </div>
      <div className="relative mt-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur px-3 py-2.5" data-testid="home-total-savings">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-blue-100">Total Simpanan</span>
          <span className="text-sm font-extrabold" data-testid="home-total-balance">{hide(b?.total)}</span>
        </div>
        <div className="grid grid-cols-2 mt-1.5 pt-1.5 border-t border-white/15">
          {locked.map(([k, l, Icon], i) => (
            <div key={k} className={`flex items-center gap-1.5 ${i ? "border-l border-white/25 pl-3" : ""}`}>
              <Icon className="h-4 w-4 shrink-0 text-blue-100" strokeWidth={1.8} />
              <div className="min-w-0">
                <div className="text-[10px] text-blue-100 flex items-center gap-1">{l} <Lock className="h-2.5 w-2.5" /></div>
                <div className="text-[11px] font-bold whitespace-nowrap tracking-tight" data-testid={`home-balance-${k}`}>{hide(b?.[k])}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-blue-100/90 mt-1.5 leading-snug" data-testid="home-locked-note">Simpanan pokok & wajib tidak dapat ditransfer atau ditarik selama masih menjadi anggota.</p>
      </div>
    </div>
  );
};

const MyQrDialog = ({ user, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xs" data-testid="my-qr-dialog">
      <DialogHeader>
        <DialogTitle className="text-center">QR Saya</DialogTitle>
        <DialogDescription className="text-center">Tunjukkan QR atau bagikan nomor rekening ini untuk menerima transfer antar anggota.</DialogDescription>
      </DialogHeader>
      <div className="mx-auto bg-white keep-white p-3 rounded-2xl shadow-md">
        <QRCodeSVG value={`CUTUAHTALINO:REK:${user.account_no || ""}`} size={190} />
      </div>
      <div className="text-center">
        <div className="font-bold text-slate-900">{user.name}</div>
        <AccountNo value={user.account_no} className="text-blue-800 text-lg justify-center" testid="my-qr-account" />
        <div className="text-[11px] text-slate-500">Simpanan Sukarela • {fmtAcc(user.account_no)}</div>
      </div>
    </DialogContent>
  </Dialog>
);

const CardDownload = ({ cardRef, user }) => {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 4, cacheBust: true, filter: (n) => !n.dataset?.exportSkip });
      const a = document.createElement("a");
      a.href = url;
      a.download = `kartu-anggota-${user.member_no}.png`;
      a.click();
      toast.success("Kartu anggota tersimpan sebagai gambar");
    } catch {
      toast.error("Gagal menyimpan kartu, coba lagi");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={save} disabled={busy} data-testid="member-card-download"
      className="-mt-1 w-full flex items-center justify-center gap-2 text-sm font-bold text-blue-800 dark:text-blue-200 bg-white border border-blue-100 dark:border-blue-900/60 rounded-2xl py-2.5 shadow-sm hover:bg-blue-50 active:scale-[0.98] transition-transform disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Simpan Kartu (Gambar)
    </button>
  );
};

export default function Beranda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useApi("/me/summary");
  const [qrOpen, setQrOpen] = useState(false);
  const cardRef = useRef(null);

  return (
    <div>
      <div className="relative bg-gradient-to-b from-blue-800 via-blue-700 to-blue-600 px-4 pb-16 -mt-px overflow-hidden" data-testid="home-hero">
        <svg className="absolute right-0 top-0 h-28 w-48 opacity-90" viewBox="0 0 200 120" aria-hidden="true">
          <path d="M200 10 C150 40 110 60 40 70 C110 70 160 55 200 35 Z" fill="#dc2626" />
          <path d="M200 0 C160 25 120 40 70 50 C130 48 170 35 200 18 Z" fill="#ffffff" opacity="0.85" />
          <path d="M90 120 L130 82 L145 95 L165 70 L200 105 V120 Z" fill="#ffffff" opacity="0.9" />
        </svg>
      </div>

      <div className="px-4 -mt-16 space-y-4 relative z-10 pt-2">
        <MemberCard user={user} cardRef={cardRef} />
        <CardDownload cardRef={cardRef} user={user} />
        <PushToggle compact />
        <BalanceCard b={data?.balances} />
        <InstallBanner />

        <div className="grid grid-cols-4 gap-2.5">
          {MENU.map(({ label, sub, icon: Icon, to, action, id, tone }, i) => (
            <button key={id} onClick={() => (action === "qr" ? setQrOpen(true) : navigate(to))} data-testid={`home-menu-${id}`}
              className="bg-white border border-slate-100 rounded-2xl py-3 px-1 min-h-[104px] flex flex-col items-center justify-center gap-2 shadow-md shadow-slate-200/70 hover:-translate-y-0.5 active:scale-[0.97] transition-transform fade-up"
              style={{ animationDelay: `${120 + i * 40}ms` }}>
              <span className={`h-11 w-11 rounded-2xl bg-gradient-to-b ${tone} text-white flex items-center justify-center shadow-lg`}><Icon className="h-6 w-6" strokeWidth={1.9} /></span>
              <span className="text-[11px] font-bold text-slate-900 text-center leading-tight">{label}{sub && <span className="block font-medium text-slate-600 text-[10px]">{sub}</span>}</span>
            </button>
          ))}
        </div>

        {data?.active_loan && (
          <button onClick={() => navigate("/app/pinjaman")} data-testid="home-active-loan" className="w-full text-left bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-2xl p-4 shadow-lg shadow-orange-500/30">
            <div className="text-xs text-white/90 font-semibold">Angsuran berikutnya</div>
            <div className="flex justify-between items-end mt-1">
              <div className="font-bold text-lg">{rp(data.active_loan.monthly_installment)}</div>
              <div className="text-xs text-white/90">Jatuh tempo {fmtDate(data.active_loan.next_due)}</div>
            </div>
          </button>
        )}

        <button onClick={() => navigate("/app/notifikasi")} data-testid="home-info-banner"
          className="w-full relative overflow-hidden flex items-center gap-3 text-left rounded-2xl pl-3 pr-4 py-3 border border-blue-100 shadow-[0_4px_14px_rgba(30,64,175,0.10)] bg-gradient-to-r from-[#e6efff] via-[#eef4ff] to-[#f7faff] dark:from-[#14213d] dark:via-[#16264a] dark:to-[#1a2c55] dark:border-blue-900/60">
          <svg className="absolute right-0 bottom-0 h-full w-56 text-blue-200/70 dark:text-blue-800/40 pointer-events-none [mask-image:linear-gradient(to_right,transparent,black_35%)]" viewBox="0 0 200 70" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 70 L52 30 L78 48 L118 12 L160 44 L200 26 L200 70 Z" fill="currentColor" />
            <path d="M40 70 L92 38 L120 54 L150 34 L200 58 L200 70 Z" fill="currentColor" opacity="0.7" />
            <path d="M112 16 L118 12 L124 17 L119 20 Z" fill="white" opacity="0.8" />
          </svg>
          <img src="/megaphone-3d.png" alt="" className="relative h-12 w-12 object-contain shrink-0 drop-shadow-md" data-testid="home-info-icon" />
          <div className="relative flex-1 min-w-0">
            <div className="font-display font-extrabold text-[#102a6b] dark:text-blue-100 text-[15px] leading-tight">Informasi CU TUAH TALINO</div>
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Pembayaran angsuran kini bisa melalui aplikasi.</div>
          </div>
          <ChevronRight className="relative h-5 w-5 text-[#102a6b] dark:text-blue-100 shrink-0" strokeWidth={2.5} />
        </button>
        <button onClick={() => navigate("/app/bantuan")} data-testid="home-help-center"
          className="w-full flex items-center gap-3 text-left rounded-2xl px-3 py-3 bg-white border border-emerald-100 shadow-[0_4px_14px_rgba(4,120,87,0.08)]">
          <span className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center shrink-0 shadow-md"><LifeBuoy className="h-6 w-6" /></span>
          <span className="flex-1 min-w-0">
            <span className="block font-display font-extrabold text-[15px] text-[#064e3b] dark:text-emerald-200">Pusat Bantuan</span>
            <span className="block text-xs text-slate-600">Pembayaran belum tercatat? Buat tiket & pantau penyelesaiannya.</span>
          </span>
          <ChevronRight className="h-5 w-5 text-[#064e3b] dark:text-emerald-200 shrink-0" strokeWidth={2.5} />
        </button>
      </div>
      <MyQrDialog user={user} open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
}
